import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLayoutEffect, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { i18n } from "src/i18n";
import authActions from "src/modules/auth/authActions";
import { default as authSelectors } from "src/modules/auth/authSelectors";
import PermissionChecker from "src/modules/auth/permissionChecker";
import layoutActions from "src/modules/layout/layoutActions";
import layoutSelectors from "src/modules/layout/layoutSelectors";
import DownloadApp from "src/modules/shared/modal/DownloadApp";
import UpgradeRequired from "src/modules/shared/modal/UpgradeRequired";
import menus from "src/view/menus";
import "./Layout.scss";

function Menu(props) {
  const dispatch = useDispatch();
  const logoUrl = `/images/logo-menu-v2.svg`;
  const {
    showDownloadAppModal,
    setShowDownloadAppModal,
    isUpgradeModaVisible,
    setIsUpgradeModalVisible,
  } = props;
  const doSignout = () => {
    dispatch(authActions.doSignout());
  };

  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const menuVisible = useSelector(layoutSelectors.selectMenuVisible);

  const doToggleMenu = () => {
    dispatch(layoutActions.doToggleMenu());
  };

  const doToggleMenuIfSmall = (e, menu) => {
    // Always close menu on mobile first
    if (window.innerWidth < 768) {
      dispatch(layoutActions.doHideMenu());
    }
    
    // Handle disabled menu items
    if (menu.isDisabled) {
      e.preventDefault();
      setIsUpgradeModalVisible(true);
      return;
    }
    
    // Handle special menu items
    if (menu.label === "Download Mobile App") {
      setShowDownloadAppModal(true);
    }
  };

  const doCloseIfSmall = () => {
    if (window.innerWidth < 768) {
      dispatch(layoutActions.doHideMenu());
    }
  };

  const doHideMenu = () => {
    dispatch(layoutActions.doHideMenu());
  };

  useLayoutEffect(() => {
    // Always close menu on mobile when component mounts
    if (window.innerWidth < 1024) {
      doHideMenu();
    }
  }, []);

  const openMenu = () => {
    dispatch(layoutActions.doShowMenu());
  };

  // Close menu when navigating to gift checkout page
  useEffect(() => {
    if (props.url === "/auth/gift/checkout") {
      doHideMenu();
    } else {
      // Only open menu on desktop (screen >= 1024px), keep closed on mobile
      if (window.innerWidth >= 1024) {
        openMenu();
      } else {
        doHideMenu();
      }
    }
  }, [props.url]);

  const permissionChecker = new PermissionChecker(currentTenant, currentUser);

  const selectedKeys = () => {
    const url = props.url;

    const match = menus().find((option) => {
      if (option.matchPaths) {
        return option.matchPaths.some(
          (matchPath) => url === matchPath || url.startsWith(matchPath + "/")
        );
      }

      if (option.exact) {
        return url === option.path;
      }

      return url === option.path || url.startsWith(option.path + "/");
    });

    if (match) {
      return [match.path];
    }

    return [];
  };

  const match = (permission) => {
    return permissionChecker.match(permission);
  };

  if (!menuVisible) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex lg:hidden">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>

        <div
          className={`relative flex w-[250px] flex-col bg-white p-[20px]`}
        >
          <div className="absolute top-0 right-0 p-1 -mr-14">
            <button
              onClick={doToggleMenu}
              className="flex items-center justify-center w-12 h-12 rounded-full focus:bg-gray-600 focus:outline-none"
            >
              <svg
                className="w-6 h-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-start flex-shrink-0">
            <Link onClick={() => doCloseIfSmall()} to="/">
              <img
                src={logoUrl}
                className="object-fit max-h-8"
                alt={i18n("app.title")}
              />
            </Link>
          </div>

          <div
            className="flex items-center justify-center flex-shrink-0 gap-3 py-3 pl-4 pr-10 mx-auto mt-8 mb-1 bg-white rounded-2xl"
            style={{
              boxShadow: "0px 4px 14px 0px rgba(0, 0, 0, 0.05)",
              width: "fit-content",
            }}
          >
            <div>
              <img
                src="/images/user-profile.png"
                className="w-12 h-12 rounded-full"
              />
            </div>
            <div>
              <p className="text-xs font-medium text-black text-opacity-30">
                Welome back
              </p>
              <h1 className="text-base font-semibold">
                <span style={{ textTransform: "capitalize" }}>
                  {currentUser.firstName + " " + currentUser.lastName}
                </span>
              </h1>
            </div>
          </div>

          <div className="flex-1 h-0 mt-5 overflow-y-auto">
            <nav className="flex flex-col h-full">
              <div className="menu-container">
                {menus()
                  .filter((menu) => match(menu.permissionRequired))
                  .map((menu, index) => {
                    const isSelected = selectedKeys().includes(menu.path);

                    return menu.href ? (
                      <a
                        className={`menu-item ${
                          menu.isDisabled ? "disabled !bg-transparent" : ""
                        }`}
                        onClick={(e) => doToggleMenuIfSmall(e, menu)}
                        key={menu.href}
                        href={menu.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="flex items-center">
                          {menu.imageIcon ? (
                            <img
                              src={menu.imageIcon}
                              className={`menu-icon ${
                                isSelected ? "filter-white" : ""
                              }`}
                              alt=""
                            />
                          ) : (
                            <FontAwesomeIcon
                              className={`menu-icon ${
                                isSelected ? "text-primary" : ""
                              }`}
                              icon={menu.icon}
                            />
                          )}
                          <span className="truncate">{menu.label}</span>
                        </div>
                        {menu.isDisabled && (
                          <img
                            src="/images/lock_white_new.svg"
                            className="menu-lock"
                            alt="Lock Icon"
                          />
                        )}
                      </a>
                    ) : (
                      <Link
                        className={`menu-item ${isSelected ? "selected" : ""} ${
                          menu.isDisabled ? "disabled !bg-transparent" : ""
                        }`}
                        onClick={(e) => doToggleMenuIfSmall(e, menu)}
                        key={menu.path}
                        to={menu.path}
                      >
                        <div className="flex items-center item">
                          {menu.imageIcon ? (
                            <img
                              src={menu.imageIcon}
                              className={`menu-icon ${
                                isSelected ? "filter-white" : ""
                              }`}
                              alt=""
                            />
                          ) : (
                            <FontAwesomeIcon
                              className={`menu-icon ${
                                isSelected ? "text-primary" : ""
                              }`}
                              icon={menu.icon}
                            />
                          )}
                          <span style={{ whiteSpace: "nowrap" }}>
                            {menu.label}
                          </span>
                        </div>

                        {menu.isDisabled && (
                          <img
                            src="/images/lock_white_new.svg"
                            className="menu-lock"
                            alt="Lock Icon"
                          />
                        )}
                      </Link>
                    );
                  })}
              </div>
              <div className="pt-5 mt-auto space-y-1">
                <div className="flex px-3 py-2 space-x-4">
                  <a
                    href="https://www.facebook.com/soaakapp"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Facebook</span>
                    <img
                      className="w-6 h-6"
                      src="/images/facebook.png"
                      alt="facebook"
                    />
                  </a>

                  <a
                    href="https://www.instagram.com/soaakapp/#"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Instagram</span>
                    <img
                      className="w-6 h-6"
                      src="/images/instagram.png"
                      alt="instagram"
                    />
                  </a>

                  <a
                    href="https://www.linkedin.com/company/soaaktechnologies"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Linkedin</span>
                    <img
                      className="w-6 h-6"
                      src="/images/linkedin.png"
                      alt="linkedin"
                    />
                  </a>

                  <a
                    href="https://www.tiktok.com/@soaakapp"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">TikTok</span>
                    <img
                      className="w-6 h-6"
                      src="/images/tiktok.png"
                      alt="tiktok"
                    />
                  </a>
                </div>

                <button
                  onClick={doSignout}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium border-l-4 border-transparent group font-jakartaMedium text-darkGreen hover:bg-gray-50"
                >
                  <img
                    className="w-[16px] h-[13px] mr-[20px] text-darkGreen"
                    src="/images/sign-out.svg"
                    alt="sign-out"
                  />
                  {i18n("auth.signout")}
                </button>
              </div>
            </nav>
          </div>
        </div>

        <div className="flex-shrink-0 w-14" aria-hidden="true"></div>
      </div>

      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex w-[250px] flex-col">
          <div className="flex flex-col flex-grow p-5 overflow-y-auto bg-white">
            <div className="flex items-center justify-start flex-shrink-0">
              <Link onClick={() => doCloseIfSmall()} to="/">
                <img
                  src={logoUrl}
                  className="object-fit h-[25px]"
                  alt={i18n("app.title")}
                />
              </Link>
            </div>

            <div className="flex flex-col flex-grow menu">
              <div className="menu-container">
                {menus()
                  .filter((menu) => match(menu.permissionRequired))
                  .map((menu, index) => {
                    const isSelected = selectedKeys().includes(menu.path);

                    return menu.href ? (
                      <a
                        className={`menu-item ${
                          menu.isDisabled ? "disabled !bg-transparent" : ""
                        } `}
                        onClick={(e) => doToggleMenuIfSmall(e, menu)}
                        key={menu.href}
                        href={menu.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="flex items-center">
                          {menu.imageIcon ? (
                            <img
                              src={menu.imageIcon}
                              className={`menu-icon ${
                                isSelected ? "filter-white" : ""
                              }`}
                              alt=""
                            />
                          ) : (
                            <FontAwesomeIcon
                              className={`menu-icon ${
                                isSelected ? "text-primary" : ""
                              }`}
                              icon={menu.icon}
                            />
                          )}
                          <span className="truncate">{menu.label}</span>
                        </div>
                        {menu.isDisabled && (
                          <img
                            src="/images/lock_white_new.svg"
                            className="menu-lock"
                            alt="Lock Icon"
                          />
                        )}
                      </a>
                    ) : (
                      <Link
                        className={`menu-item ${isSelected ? "selected" : ""} ${
                          menu.isDisabled ? "disabled !bg-transparent" : ""
                        }`}
                        onClick={(e) => doToggleMenuIfSmall(e, menu)}
                        key={menu.path}
                        to={menu.path}
                      >
                        <div className="flex items-center item">
                          {menu.imageIcon ? (
                            <img
                              src={menu.imageIcon}
                              className={`menu-icon ${
                                isSelected ? "filter-white" : ""
                              }`}
                              alt=""
                            />
                          ) : (
                            <FontAwesomeIcon
                              className={`menu-icon ${
                                isSelected ? "text-primary" : ""
                              }`}
                              icon={menu.icon}
                            />
                          )}
                          <span style={{ whiteSpace: "nowrap" }}>
                            {menu.label}
                          </span>
                        </div>

                        {menu.isDisabled && (
                          <img
                            src="/images/lock_white_new.svg"
                            className="menu-lock"
                            alt="Lock Icon"
                          />
                        )}
                      </Link>
                    );
                  })}
              </div>
              <div className="flex-shrink-0 block w-full">
                <div className="flex px-3 py-2 space-x-[10px] opacity-60">
                  <a
                    href="https://www.tiktok.com/@soaakapp"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">TikTok</span>
                    <img
                      className="w-[18px] h-[18px]"
                      src="/images/tiktok.png"
                      alt="tiktok"
                    />
                  </a>

                  <a
                    href="https://www.facebook.com/soaakapp"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Facebook</span>
                    <img
                      className="w-[18px] h-[18px]"
                      src="/images/facebook.png"
                      alt="facebook"
                    />
                  </a>

                  <a
                    href="https://www.instagram.com/soaakapp/#"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Instagram</span>
                    <img
                      className="w-[18px] h-[18px]"
                      src="/images/instagram.png"
                      alt="instagram"
                    />
                  </a>

                  <a
                    href="https://www.linkedin.com/company/soaaktechnologies"
                    className="text-darkGreen"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Linkedin</span>
                    <img
                      className="w-[18px] h-[18px]"
                      src="/images/linkedin.png"
                      alt="linkedin"
                    />
                  </a>
                </div>

                <button
                  onClick={doSignout}
                  className="flex items-center w-full px-2 py-2 text-[12px] font-medium border-l-4 border-transparent group font-jakartaMedium text-darkGreen hover:bg-gray-50 opacity-60"
                >
                  <img
                    className="w-[16px] h-[13px] mr-[20px] text-darkGreen"
                    src="/images/sign-out.svg"
                    alt="sign-out"
                  />
                  {i18n("auth.signout")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Menu;
